/**
 * Step in the avatar creation flow.
 *
 * @enum {string}
 */
export enum AvatarCreateStep {
  CONFIRMATION = 'confirmation',
  LOADING = 'loading',
  PHOTO_SELECTION = 'photo-selection'
}

/**
 * Status of avatar creation/processing.
 *
 * @enum {string}
 */
export enum AvatarCreateStatus {
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  IDLE = 'IDLE',
  PROCESSING = 'PROCESSING',
  PROCESSING_BACKGROUND = 'PROCESSING_BACKGROUND',
  UPLOADING = 'UPLOADING'
}

/**
 * Source type for avatar photo.
 *
 * @enum {string}
 */
export enum AvatarSourceType {
  CAMERA = 'CAMERA',
  GALLERY = 'GALLERY'
}

/**
 * Selected image data.
 *
 * @interface SelectedImage
 */
export interface SelectedImage {
  blob?: Blob;
  dataUrl: string | null;
  sourceType: AvatarSourceType | null;
}

/**
 * State interface for avatar create page.
 *
 * @exports
 * @interface AvatarCreateState
 */
export interface AvatarCreateState {
  error: string | null;
  avatarId: string | null;
  pollingAttempts: number;
  maxPollingAttempts: number;
  maxPollingDuration: number; // milliseconds
  status: AvatarCreateStatus;
  selectedImage: SelectedImage;
  currentStep: AvatarCreateStep;
  validationError: string | null;
  pollingStartTime: number | null;
  loading: {
    takingPhoto: boolean;
    selectingFromGallery: boolean;
    uploading: boolean;
    polling: boolean;
  };
}

/**
 * Feature key for avatar create state.
 */
export const avatarCreateKey = 'avatarCreate';

/**
 * Initial state for avatar create.
 */
export const avatarCreateState: AvatarCreateState = {
  currentStep: AvatarCreateStep.PHOTO_SELECTION,
  selectedImage: {
    dataUrl: null,
    sourceType: null
  },
  avatarId: null,
  status: AvatarCreateStatus.IDLE,
  validationError: null,
  loading: {
    takingPhoto: false,
    selectingFromGallery: false,
    uploading: false,
    polling: false
  },
  error: null,
  pollingAttempts: 0,
  maxPollingAttempts: 5, // Poll max 5 times (2-3 seconds each = ~10-15 seconds)
  pollingStartTime: null,
  maxPollingDuration: 10000 // 10 seconds
};
